import { Outlet, useLocation, useNavigate } from 'react-router';
import { showError } from '@/libs/react.toastify';
import { AuthMutations } from '@/features/auth/login/services/mutation';
import { useAuth } from '@/zustand/auth-store';
import tw from 'twin.macro';

const HEADER_HIDDEN_ROUTES = new Set([
  '/post-performance',
  '/try-demo',
  '/exercise',
  '/camera-permission',
]);

const ProtectedLayout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const clearAuth = useAuth(state => state.clearAuth);
  const accessToken = useAuth(state => state.accessToken);
  const shouldHideHeader = HEADER_HIDDEN_ROUTES.has(location.pathname);
  // This layout also wraps the guard-free routes now, so it can render for a
  // visitor with no session — offer Login there instead of Logout.
  const isSignedIn = Boolean(accessToken);

  const handleLogoutSuccess = () => {
    clearAuth();
    navigate('/login', { replace: true });
  };

  const handleLogoutError = error => {
    clearAuth();
    navigate('/login', { replace: true });

    const message = error?.response?.data?.message;
    if (message) {
      showError(message);
    }
  };

  const { mutate: logoutUser, isPending } = AuthMutations.useLogoutUser({
    onSuccess: handleLogoutSuccess,
    onError: handleLogoutError,
  });

  const handleLogout = () => {
    logoutUser();
  };

  return (
    <div css={tw`min-h-screen bg-neutral_50 flex flex-col`}>
      {!shouldHideHeader && (
        <header
          css={tw`sticky top-0 z-[1] flex items-center justify-between border-b border-border_stroke bg-white px-6 py-4 max-md:px-4`}
        >
          <div>
            <h1 css={tw`text-lg font-semibold text-text_primary`}>Movex Studio</h1>
          </div>
          {isSignedIn ? (
            <button
              type="button"
              onClick={handleLogout}
              disabled={isPending}
              css={tw`rounded-md border border-border_stroke px-4 py-2 text-sm font-semibold text-text_primary transition hover:bg-neutral_50 disabled:cursor-not-allowed disabled:opacity-60`}
            >
              {isPending ? 'Logging out...' : 'Logout'}
            </button>
          ) : (
            <button
              type="button"
              onClick={() => navigate('/login')}
              css={tw`rounded-md border border-border_stroke px-4 py-2 text-sm font-semibold text-text_primary transition hover:bg-neutral_50`}
            >
              Login
            </button>
          )}
        </header>
      )}
      <main css={tw`flex-1 min-h-0`}>
        <Outlet />
      </main>
    </div>
  );
};

export default ProtectedLayout;
